import { CheckOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons"
import {
  Button,
  Checkbox,
  Empty,
  Flex,
  Modal,
  Segmented,
  Tooltip,
  Typography
} from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../../components/Input/TextInput.tsx"
import type { CatalogWidgetSpec } from "../../redux/widgets.types"
import { homeColor, homeSize, homeText } from "../../home-theme.ts"
import {
  WIDGET_CATEGORIES,
  type WidgetCategory,
  categoryOf,
  describeSpec
} from "./widgetCatalogCategories.ts"
import { WidgetPreview } from "./WidgetPreview.tsx"

export interface CatalogEntry {
  /** The stable widget id (its slug). Deduplication happens on this, never on the title. */
  id: string
  title: string
  spec: CatalogWidgetSpec
  /** Already on the draft dashboard, so it cannot be added again. */
  alreadyAdded: boolean
}

interface AddWidgetsModalProps {
  open: boolean
  entries: CatalogEntry[]
  onAdd: (ids: string[]) => void
  onClose: () => void
}

const CARD_MIN = 260

/** The English a missing translation falls back to, rather than the raw key. */
const CATEGORY_FALLBACK: Record<WidgetCategory, string> = {
  all: "All",
  projects: "Projects",
  finance: "Finance",
  leads: "Leads",
  activity: "Activity",
  other: "Other"
}

/**
 * The widget picker.
 *
 * It replaces a 420px side drawer holding one long alphabetical list, which
 * made a catalog of forty widgets unreadable and gave no way to tell two
 * similarly named ones apart. The body scrolls on its own so the header's
 * search and the footer's Add never leave the viewport.
 *
 * Selections are held here and committed on Add: closing discards only what
 * was ticked in the picker, never the dashboard edits underneath it.
 */
export const AddWidgetsModal = ({
  open,
  entries,
  onAdd,
  onClose
}: AddWidgetsModalProps) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const [category, setCategory] = useState<WidgetCategory>("all")
  const [selected, setSelected] = useState<string[]>([])
  // Off by default: the picker is for what you can still add. A catalog whose
  // every card is disabled answers no question a person came here with.
  const [showAdded, setShowAdded] = useState(false)

  // A fresh picker every time: a selection left over from a cancelled session
  // would be added on the next Add without the user ever seeing it ticked.
  useEffect(() => {
    if (open) {
      setQuery("")
      setCategory("all")
      setSelected([])
      setShowAdded(false)
    }
  }, [open])

  // One entry per widget id. Two specs really can share a title (the backend
  // ships per-source variants), so the key is the id and both survive.
  const unique = useMemo(() => {
    const byId = new Map<string, CatalogEntry>()
    for (const entry of entries)
      if (!byId.has(entry.id)) byId.set(entry.id, entry)
    return [...byId.values()]
  }, [entries])

  // Everything the toggle currently admits — the pool the tabs count and the
  // search runs over, so a category tab never promises rows it will not show.
  const pool = useMemo(
    () => (showAdded ? unique : unique.filter((entry) => !entry.alreadyAdded)),
    [unique, showAdded]
  )

  const availableCount = useMemo(
    () => unique.filter((entry) => !entry.alreadyAdded).length,
    [unique]
  )
  const addedCount = unique.length - availableCount

  const counts = useMemo(() => {
    const tally = new Map<WidgetCategory, number>()
    for (const entry of pool) {
      const key = categoryOf(entry.spec)
      tally.set(key, (tally.get(key) ?? 0) + 1)
    }
    return tally
  }, [pool])

  // An empty category would be a dead tab, so only the ones with widgets show.
  const tabs = useMemo(
    () =>
      WIDGET_CATEGORIES.filter(
        (key) => key === "all" || (counts.get(key) ?? 0) > 0
      ).map((key) => ({
        value: key,
        label: t(`portfolio.catalog.category.${key}`, CATEGORY_FALLBACK[key])
      })),
    [counts, t]
  )

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return pool
      .filter(
        (entry) => category === "all" || categoryOf(entry.spec) === category
      )
      .filter(
        (entry) =>
          !needle ||
          entry.title.toLowerCase().includes(needle) ||
          describeSpec(entry.spec, t).toLowerCase().includes(needle)
      )
      .sort((a, b) => a.title.localeCompare(b.title))
  }, [pool, category, query, t])

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )

  const count = selected.length

  const footer = (
    <Flex align="center" justify="space-between" gap={12} wrap="wrap">
      <div
        style={{
          ...homeText.meta,
          color: homeColor.inkMuted,
          textAlign: "start"
        }}
      >
        <div data-testid="add-widgets-count">
          {count === 1
            ? t("portfolio.catalog.selectedOne", "1 widget selected")
            : t(
                "portfolio.catalog.selectedMany",
                "{{count}} widgets selected",
                { count }
              )}
        </div>
        <div>
          {t("portfolio.catalog.saveHint", "Save layout to keep your changes.")}
        </div>
      </div>
      <Flex gap={8}>
        <Button onClick={onClose}>
          {t("portfolio.editLayout.cancel", "Cancel")}
        </Button>
        <Button
          type="primary"
          disabled={count === 0}
          data-testid="add-widgets-submit"
          onClick={() => onAdd(selected)}
        >
          {count === 0
            ? t("portfolio.catalog.addNone", "Add widgets")
            : count === 1
              ? t("portfolio.catalog.addOne", "Add 1 widget")
              : t("portfolio.catalog.addMany", "Add {{count}} widgets", {
                  count
                })}
        </Button>
      </Flex>
    </Flex>
  )

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width="min(1060px, calc(100vw - 32px))"
      styles={{
        body: {
          maxHeight: "calc(85vh - 196px)",
          overflowY: "auto",
          overflowX: "hidden"
        }
      }}
      footer={footer}
      title={
        <div>
          <div style={{ ...homeText.sectionTitle, color: homeColor.ink }}>
            {t("portfolio.catalog.title", "Add widgets")}
          </div>
          <div
            style={{
              ...homeText.meta,
              color: homeColor.inkMuted,
              fontWeight: 400
            }}
          >
            {t(
              "portfolio.catalog.subtitle",
              "Choose what appears on your dashboard."
            )}
          </div>
        </div>
      }
    >
      <Flex vertical gap={16} style={{ paddingTop: 4 }}>
        <TextInput
          allowClear
          value={query}
          prefix={<SearchOutlined style={{ color: homeColor.inkMuted }} />}
          placeholder={t("portfolio.catalog.search", "Search widgets…")}
          aria-label={t("portfolio.catalog.search", "Search widgets…")}
          onChange={(event) => setQuery(event.target.value)}
          style={{ height: homeSize.controlHeight }}
        />

        <Flex align="center" justify="space-between" gap={12} wrap="wrap">
          <div style={{ overflowX: "auto", minWidth: 0 }}>
            <Segmented
              value={category}
              options={tabs}
              onChange={(value) => setCategory(value as WidgetCategory)}
            />
          </div>
          {addedCount > 0 && (
            <Checkbox
              checked={showAdded}
              data-testid="show-added-widgets"
              onChange={(event) => setShowAdded(event.target.checked)}
            >
              <span style={{ ...homeText.body, color: homeColor.inkMuted }}>
                {t("portfolio.catalog.showAdded", "Show added widgets")}
              </span>
            </Checkbox>
          )}
        </Flex>

        {shown.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              // "Nothing left to add" and "your search matched nothing" are
              // different answers and a person acts on them differently.
              !showAdded && availableCount === 0 && !query.trim()
                ? t(
                    "portfolio.catalog.allAdded",
                    "All available widgets are already on your dashboard."
                  )
                : t("portfolio.catalog.noResults", "No widgets found")
            }
          >
            {!showAdded && availableCount === 0 && (
              <Button onClick={() => setShowAdded(true)}>
                {t("portfolio.catalog.showAdded", "Show added widgets")}
              </Button>
            )}
          </Empty>
        ) : (
          <div
            data-testid="widget-catalog-grid"
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN}px, 1fr))`
            }}
          >
            {shown.map((entry) => {
              const isSelected = selected.includes(entry.id)
              return (
                <div
                  key={entry.id}
                  data-testid={`widget-card-${entry.id}`}
                  style={{
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    padding: 12,
                    borderRadius: homeSize.cardRadius,
                    background: homeColor.surface,
                    border: `1px solid ${isSelected ? homeColor.teal : homeColor.border}`,
                    boxShadow: isSelected
                      ? `0 0 0 1px ${homeColor.teal}`
                      : undefined
                  }}
                >
                  <WidgetPreview
                    type={entry.spec.type}
                    metric={entry.spec.metric}
                  />
                  <div
                    style={{ ...homeText.actionTitle, color: homeColor.ink }}
                  >
                    {entry.title}
                  </div>
                  <Typography.Paragraph
                    style={{
                      ...homeText.meta,
                      color: homeColor.inkMuted,
                      margin: 0
                    }}
                    ellipsis={{ rows: 2, tooltip: describeSpec(entry.spec, t) }}
                  >
                    {describeSpec(entry.spec, t)}
                  </Typography.Paragraph>
                  <div style={{ marginTop: "auto" }}>
                    {entry.alreadyAdded ? (
                      <Tooltip
                        title={t(
                          "portfolio.catalog.alreadyAddedHint",
                          "This widget is already on your dashboard."
                        )}
                      >
                        <Button block disabled icon={<CheckOutlined />}>
                          {t("portfolio.catalog.added", "Added")}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        block
                        type={isSelected ? "primary" : "default"}
                        icon={isSelected ? <CheckOutlined /> : <PlusOutlined />}
                        aria-pressed={isSelected}
                        onClick={() => toggle(entry.id)}
                      >
                        {isSelected
                          ? t("portfolio.catalog.selected", "Selected")
                          : t("portfolio.catalog.select", "Select")}
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Flex>
    </Modal>
  )
}
